import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { SignedIn, SignedOut, SignInButton, UserButton, useUser, useAuth } from '@clerk/clerk-react';
import {
  AppBar,
  Toolbar,
  Container,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Alert,
  CircularProgress,
  Menu as MuiMenu,
  MenuItem,
  Dialog,
  DialogContent,
  Avatar,
} from '@mui/material';
import { 
  Menu as MenuIcon,
  Search as SearchIcon,
  Bookmark as BookmarkIcon,
  User as PersonIcon,
  Star as StarIcon,
  Lock as LockIcon,
  LayoutDashboard as DashboardIcon,
  ChevronDown as ChevronDownIcon,
} from 'lucide-react';
import JobList from './components/JobList';
import JobForm from './components/JobForm';
import JobSearch from './components/JobSearch';
import Profile from './components/Profile';
import GoPro from './components/GoPro';
import FeedbackWidget from './components/FeedbackWidget';
import ShareWidget from './components/ShareWidget';
import NavbarTitle from './components/NavbarTitle';
// RoleSelection modal removed — role switching now handled by navbar dropdown
import NotificationBell from './components/NotificationBell';
// Feed hidden — not part of current product
// import Feed from './components/Feed';
import { getJobs, createJob, updateJob, deleteJob, setClerkTokenGetter, getInit, setRole } from './services/api';
import { trackFunnelEvent, rememberSignInIntent, consumeSignInIntent } from './services/funnel';
import { fbTrack } from './services/pixel';
import { layoutPatterns, designSystem } from './theme';

// Feature flags
const ENABLE_ROLES = false;
const ENABLED_ROLES = ['seeker'];
// Feed disabled — not part of current product
const MARKETING_LINKS = [
  { href: '/jobs/', label: 'Data Research' },
];
const INIT_CACHE_KEY = 'jjj_last_init_payload';

const normalizeRole = (role) => (ENABLED_ROLES.includes(role) ? role : 'seeker');
const getRoleFromPath = (pathname) => {
  if (pathname.startsWith('/jobmatcher') || pathname.startsWith('/matcher')) return 'seeker';
  if (pathname.startsWith('/jobseeker')) return 'seeker';
  return null;
};
const readCachedInit = () => {
  try {
    const raw = window.sessionStorage.getItem(INIT_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeCachedInit = (data) => {
  try {
    window.sessionStorage.setItem(INIT_CACHE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage failures; live init data still works.
  }
};

// Component to set up Clerk token for API calls
function ClerkTokenProvider({ children }) {
  const { getToken } = useAuth();
  
  useEffect(() => {
    setClerkTokenGetter(getToken);
  }, [getToken]);
  
  return children;
}

// Protected route wrapper - shows sign-in prompt if not authenticated
function ProtectedRoute({ children }) {
  const { isSignedIn, isLoaded } = useUser();

  if (!isLoaded) {
    return (
      <Box sx={layoutPatterns.centerContent}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSignedIn) {
    return (
      <Container maxWidth="sm" sx={{ textAlign: 'center', mt: 8 }}>
        <Box sx={{ ...designSystem.card.light, p: 4 }}>
          <Box display="flex" justifyContent="center" mb={3}>
            <Box sx={{ 
              width: 80, 
              height: 80, 
              borderRadius: '50%', 
              bgcolor: 'primary.50', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
            }}>
              <LockIcon size={40} strokeWidth={1.5} color="#1e88e5" />
            </Box>
          </Box>
          <Typography variant="h4" component="h2" gutterBottom fontWeight="600" sx={{ color: 'text.primary' }}>
            Sign in Required
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
            Create a free account to access this feature.
          </Typography>
          <SignInButton mode="modal">
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => {
                rememberSignInIntent('protected_route');
                trackFunnelEvent('sign_in_clicked', { source: 'protected_route' });
              }}
              sx={designSystem.button.contained}
            >
              Create Free Account to Continue
            </Button>
          </SignInButton>
          <Box sx={{ mt: 3, textAlign: 'left', display: 'inline-block' }}>
            <Typography variant="body2" color="text.secondary" lineHeight={2.5}>
              ✓ Save jobs to your dashboard<br />
              ✓ Track application status<br />
              ✓ Get personalized job matches
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return children;
}

/** Fire PageView every time the React route changes. */
function RouteTracker() {
  const location = useLocation();
  const { isSignedIn, user } = useUser();
  const pageStartRef = useRef(Date.now());
  const currentPathRef = useRef(`${location.pathname}${location.search}`);
  const initializedRef = useRef(false);
  const shouldSkipTracking = location.pathname === '/';

  useEffect(() => {
    if (shouldSkipTracking) return;

    const handlePageHide = () => {
      const durationMs = Date.now() - pageStartRef.current;
      trackFunnelEvent(
        'page_leave',
        { duration_ms: durationMs, exit_reason: 'pagehide' },
        {
          clerkUserId: user?.id,
          path: currentPathRef.current,
          useBeacon: true,
        }
      );
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [shouldSkipTracking, user?.id]);

  useEffect(() => {
    const nextPath = `${location.pathname}${location.search}`;
    const now = Date.now();

    if (shouldSkipTracking) {
      pageStartRef.current = now;
      currentPathRef.current = nextPath;
      initializedRef.current = false;
      return;
    }

    if (initializedRef.current) {
      trackFunnelEvent(
        'page_leave',
        { duration_ms: now - pageStartRef.current, exit_reason: 'route_change' },
        {
          clerkUserId: user?.id,
          path: currentPathRef.current,
        }
      );
    } else {
      initializedRef.current = true;
    }

    pageStartRef.current = now;
    currentPathRef.current = nextPath;

    fbTrack('PageView');
    trackFunnelEvent(
      'page_view',
      {
        pathname: location.pathname,
        search: location.search || null,
        is_signed_in: isSignedIn,
      },
      {
        clerkUserId: user?.id,
        path: nextPath,
      }
    );
  }, [location.pathname, location.search]);

  return null;
}

function Navigation({ jobCount, user, profile, resultsCount, suggestedTitles, isPro, userRole, onRoleChange }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [roleMenuAnchor, setRoleMenuAnchor] = useState(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const trackSignInClick = useCallback((source) => {
    rememberSignInIntent(source);
    trackFunnelEvent('sign_in_clicked', { source });
  }, []);

  // Close menu when route changes
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  // Go Pro CTA — appears in every role's nav ($4 flat, unlocks LinkedIn integration for all)
  const goProItem = { path: '/gopro', label: 'Go Pro', icon: <StarIcon size={18} />, color: 'secondary', pro: true };

  // Each role is an isolated app with its own nav — no blending
  const roleNavs = {
    seeker: [
      goProItem,
      { path: '/jobseeker', label: 'Find Jobs', icon: <SearchIcon size={18} /> },
      { path: '/jobseeker/saved', label: `Saved Jobs (${jobCount})`, icon: <BookmarkIcon size={18} /> },
      { path: '/jobseeker/profile', label: 'Profile', icon: <PersonIcon size={18} /> },
    ],
    matcher: [
      goProItem,
      { path: '/jobmatcher', label: 'Match Jobs', icon: <DashboardIcon size={18} /> },
    ],
  };

  // Role display config for the switcher dropdown
  const roleConfig = {
    seeker: { label: 'Job Seeker', icon: <SearchIcon size={16} />, home: '/jobseeker' },
    matcher: { label: 'Job Matcher', icon: <DashboardIcon size={16} />, home: '/jobmatcher' },
  };
  const routeRole = getRoleFromPath(location.pathname);
  const effectiveUserRole = ENABLE_ROLES ? normalizeRole(routeRole || userRole) : 'seeker';
  const currentRoleConfig = roleConfig[effectiveUserRole] || roleConfig.seeker;
  const navItems = roleNavs[effectiveUserRole] || roleNavs.seeker;

  const isActive = (path) => location.pathname === path;
  const navButtonSx = (active = false, pro = false) => ({
    borderRadius: 2,
    fontWeight: pro ? 700 : 600,
    textTransform: 'none',
    borderColor: active ? (pro ? 'secondary.main' : 'primary.main') : 'grey.300',
    color: active ? 'white' : (pro ? 'secondary.main' : 'text.primary'),
    bgcolor: active ? (pro ? 'secondary.main' : 'primary.main') : 'transparent',
    '&:hover': {
      borderColor: pro ? 'secondary.main' : 'primary.main',
      bgcolor: active ? (pro ? 'secondary.dark' : 'primary.dark') : 'grey.50',
    },
  });
  const mobileNavItemSx = (active = false, pro = false) => ({
    borderRadius: 2,
    mx: 1,
    mb: 0.5,
    border: '1px solid',
    borderColor: active ? (pro ? 'secondary.main' : 'primary.main') : 'grey.300',
    color: active ? 'white' : (pro ? 'secondary.main' : 'text.primary'),
    bgcolor: active ? (pro ? 'secondary.main' : 'primary.main') : 'transparent',
    '&:hover': {
      borderColor: pro ? 'secondary.main' : 'primary.main',
      bgcolor: active ? (pro ? 'secondary.dark' : 'primary.dark') : 'grey.50',
    },
  });

  const handleRoleSwitch = async (role) => {
    setRoleMenuAnchor(null);
    const nextRole = normalizeRole(role);
    if (nextRole !== userRole && onRoleChange) {
      // Await role change so backend has the new role before we navigate
      await onRoleChange(nextRole);
    }
    const home = roleConfig[nextRole]?.home || '/jobseeker';
    if (location.pathname !== home) navigate(home);
  };
  
  return (
    <>
      <AppBar position="sticky" color="inherit" elevation={0}>
        <Toolbar sx={{ py: 1 }}>
          <NavbarTitle 
            user={user}
            profile={profile}
            resultsCount={resultsCount}
            suggestedTitles={suggestedTitles}
            isPro={isPro}
          />

          <Box flexGrow={1} />

          <SignedIn>
            {/* Role Switcher Dropdown (Handshake-style) — desktop only */}
            {ENABLE_ROLES && (
              <Box sx={{ mr: 1, display: { xs: 'none', md: 'block' } }}>
                <Button
                  onClick={(e) => setRoleMenuAnchor(e.currentTarget)}
                  endIcon={<ChevronDownIcon size={14} />}
                  startIcon={currentRoleConfig.icon}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    color: 'text.primary',
                    bgcolor: 'grey.100',
                    borderRadius: 2,
                    px: 1.5,
                    '&:hover': { bgcolor: 'grey.200' },
                  }}
                >
                  {currentRoleConfig.label}
                </Button>
                <MuiMenu
                  anchorEl={roleMenuAnchor}
                  open={Boolean(roleMenuAnchor)}
                  onClose={() => setRoleMenuAnchor(null)}
                  PaperProps={{ sx: { borderRadius: 2, mt: 1, minWidth: 200 } }}
                >
                  {Object.entries(roleConfig).map(([key, config]) => (
                    <MenuItem
                      key={key}
                      onClick={() => handleRoleSwitch(key)}
                      selected={effectiveUserRole === key}
                      sx={{ gap: 1.5, py: 1.5 }}
                    >
                      {config.icon}
                      <Typography variant="body2" fontWeight={effectiveUserRole === key ? 600 : 400}>
                        {config.label}
                      </Typography>
                    </MenuItem>
                  ))}
                </MuiMenu>
              </Box>
            )}

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
              {MARKETING_LINKS.map((item) => (
                <Button
                  key={item.href}
                  component="a"
                  href={item.href}
                  variant="outlined"
                  size="small"
                  sx={navButtonSx(false, false)}
                >
                  {item.label}
                </Button>
              ))}
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  startIcon={item.icon}
                  variant={isActive(item.path) ? 'contained' : 'outlined'}
                  color={item.pro ? 'secondary' : isActive(item.path) ? 'primary' : 'inherit'}
                  sx={navButtonSx(isActive(item.path), item.pro)}
                  size="small"
                >
                  {item.label}
                </Button>
              ))}
              <NotificationBell />
              <UserButton afterSignOutUrl="/" />
            </Box>

            {/* Mobile Hamburger */}
            <IconButton
              sx={{ display: { xs: 'block', md: 'none' } }}
              onClick={toggleMobileMenu}
              edge="end"
            >
              <MenuIcon />
            </IconButton>
          </SignedIn>

          <SignedOut>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
              {MARKETING_LINKS.map((item) => (
                <Button
                  key={item.href}
                  component="a"
                  href={item.href}
                  variant="outlined"
                  sx={navButtonSx(false, false)}
                >
                  {item.label}
                </Button>
              ))}
              <SignInButton mode="modal">
                <Button
                  variant="contained"
                  onClick={() => trackSignInClick('navbar_desktop')}
                >
                  Create Free Account
                </Button>
              </SignInButton>
            </Box>
            <IconButton
              sx={{ display: { xs: 'block', md: 'none' } }}
              onClick={toggleMobileMenu}
              edge="end"
            >
              <MenuIcon />
            </IconButton>
          </SignedOut>
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
      >
        <Box sx={{ width: 280, pt: 2 }}>
          <SignedIn>
            {/* Mobile Role Switcher */}
            {ENABLE_ROLES && (
              <Box sx={{ px: 2, pb: 1.5, mb: 1, borderBottom: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="caption" color="text.secondary" fontWeight="600" sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 1, display: 'block' }}>
                  Switch Role
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {Object.entries(roleConfig).map(([key, config]) => (
                    <Button
                      key={key}
                      size="small"
                      variant={effectiveUserRole === key ? 'contained' : 'outlined'}
                      color={effectiveUserRole === key ? 'primary' : 'inherit'}
                      startIcon={config.icon}
                      onClick={() => {
                        handleRoleSwitch(key);
                        closeMobileMenu();
                      }}
                      sx={{
                        flex: 1,
                        textTransform: 'none',
                        fontWeight: effectiveUserRole === key ? 700 : 400,
                        fontSize: '0.7rem',
                        px: 0.5,
                        minWidth: 0,
                        borderRadius: 2,
                      }}
                    >
                      {key === 'seeker' ? 'Seeker' : 'Matcher'}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}

            <List>
              {MARKETING_LINKS.map((item) => (
                <ListItem key={item.href} disablePadding>
                  <ListItemButton
                    component="a"
                    href={item.href}
                    onClick={closeMobileMenu}
                    sx={mobileNavItemSx(false, false)}
                  >
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
              {navItems.map((item) => (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    onClick={closeMobileMenu}
                    selected={isActive(item.path)}
                    sx={mobileNavItemSx(isActive(item.path), item.pro)}
                  >
                    {item.icon}
                    <ListItemText primary={item.label} sx={{ ml: 2 }} />
                  </ListItemButton>
                </ListItem>
              ))}
              <ListItem sx={{ px: 2, py: 1 }}>
                <NotificationBell />
                <Typography variant="body2" sx={{ ml: 2 }}>Notifications</Typography>
              </ListItem>
              <ListItem>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
                  <UserButton afterSignOutUrl="/" />
                  <Typography variant="body2">Account</Typography>
                </Box>
              </ListItem>
            </List>
          </SignedIn>
          <SignedOut>
            <Box sx={{ p: 2 }}>
              {MARKETING_LINKS.map((item) => (
                <Button
                  key={item.href}
                  component="a"
                  href={item.href}
                  variant="outlined"
                  fullWidth
                  onClick={closeMobileMenu}
                  sx={{ mb: 1.5 }}
                >
                  {item.label}
                </Button>
              ))}
              <SignInButton mode="modal">
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    trackSignInClick('navbar_mobile');
                    closeMobileMenu();
                  }}
                >
                  Create Free Account
                </Button>
              </SignInButton>
            </Box>
          </SignedOut>
        </Box>
      </Drawer>
    </>
  );
}

function MyJobs({ jobs, loading, onEdit, onDelete, showForm, setShowForm, editingJob, setEditingJob, handleCreateJob, handleUpdateJob }) {
  const handleCancelEdit = () => {
    setEditingJob(null);
    setShowForm(false);
  };

  return (
    <Box>
      <Box sx={{ ...layoutPatterns.flexRowSpaceBetween, ...layoutPatterns.pageHeader }}>
        <Typography variant="h4" component="h2" fontWeight="600" sx={{ color: 'text.primary' }}>
          Saved Jobs
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            setEditingJob(null);
            setShowForm(!showForm);
          }}
          sx={designSystem.button.contained}
        >
          {showForm ? 'Cancel' : '+ Add Job Manually'}
        </Button>
      </Box>

      {showForm && (
        <JobForm
          job={editingJob}
          onSubmit={editingJob ? 
            (data) => handleUpdateJob(editingJob.id, data) : 
            handleCreateJob
          }
          onCancel={handleCancelEdit}
        />
      )}

      {loading ? (
        <Box sx={{ ...layoutPatterns.centerContent, py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <JobList
          jobs={jobs}
          onEdit={onEdit}
          onUpdate={handleUpdateJob}
          onDelete={onDelete}
        />
      )}
    </Box>
  );
}

function WelcomeModal() {
  const { isSignedIn, isLoaded } = useUser();
  const [dismissed, setDismissed] = useState(false);

  // Track FB traffic for analytics — no behavioral gating
  const isFromFacebook = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('utm_source') === 'facebook' || params.has('fbclid');
  }, []);

  // Reset dismissed state when user signs out so modal shows again next visit
  useEffect(() => {
    if (isSignedIn) setDismissed(false);
  }, [isSignedIn]);

  const fromFacebook = isFromFacebook();

  useEffect(() => {
    if (isLoaded && !isSignedIn && fromFacebook) {
      trackFunnelEvent('welcome_modal_skipped', { reason: 'facebook_traffic' });
      return;
    }
    if (isLoaded && !isSignedIn && !dismissed) {
      trackFunnelEvent('welcome_modal_viewed', { from_facebook: fromFacebook });
    }
  }, [dismissed, fromFacebook, isLoaded, isSignedIn]);

  const handleDismiss = useCallback(() => {
    trackFunnelEvent('welcome_modal_dismissed', { from_facebook: fromFacebook });
    setDismissed(true);
  }, [fromFacebook]);

  const trackWelcomeSignInClick = useCallback((source) => {
    rememberSignInIntent(source);
    trackFunnelEvent('sign_in_clicked', { source, from_facebook: fromFacebook });
  }, [fromFacebook]);

  if (!isLoaded || isSignedIn) return null;

  // Facebook ad traffic should land directly in job search without an interstitial.
  if (fromFacebook) return null;

  return (
    <>
      <Dialog
        open={!dismissed}
        onClose={handleDismiss}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { textAlign: 'center', overflow: 'hidden' } }}
      >
        <Box sx={{ bgcolor: 'primary.main', py: 4, px: 3 }}>
          <Avatar
            alt="Gerald"
            src="/gerald-v5-crisp.jpg"
            variant="rounded"
            sx={{
              width: 120,
              height: 120,
              mx: 'auto',
              mb: 2,
              borderRadius: 2,
              border: '3px solid rgba(255,255,255,0.6)',
            }}
          />
          <Typography variant="h6" fontWeight={700} sx={{ color: 'white', mb: 1 }}>
            Hi, my name is Gerald
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.7 }}>
            I am a real person - I needed help finding a job and so I created this. I hope it helps you as well.
          </Typography>
        </Box>
        <DialogContent sx={{ px: 3, pt: 3, pb: 3 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleDismiss}
            sx={{ fontWeight: 700, py: 1.5, fontSize: '1rem', mb: 1.5 }}
          >
            Let&apos;s Find Jobs →
          </Button>
          <SignInButton mode="modal">
            <Button
              variant="text"
              size="small"
              fullWidth
              onClick={() => trackWelcomeSignInClick('welcome_modal')}
              sx={{ color: 'text.secondary', fontSize: '0.8rem' }}
            >
              Sign in to stop seeing this
            </Button>
          </SignInButton>
        </DialogContent>
      </Dialog>

    </>
  );
}

function RootRedirect() {
  const location = useLocation();
  return <Navigate to={`/jobseeker${location.search}`} replace />;
}

function RoleRouteSync({ userRole, onRoleChange }) {
  const location = useLocation();
  const { isLoaded } = useUser();

  useEffect(() => {
    if (!isLoaded) return;
    const routeRole = getRoleFromPath(location.pathname);
    if (!routeRole || routeRole === userRole) return;
    onRoleChange(routeRole);
  }, [isLoaded, location.pathname, onRoleChange, userRole]);

  return null;
}

function App() {
  const { isSignedIn, isLoaded, user } = useUser();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [activeWidget, setActiveWidget] = useState(null); // 'share', 'feedback', or null
  const [navbarContext, setNavbarContext] = useState({
    profile: null,
    resultsCount: 0,
    suggestedTitles: [],
    isPro: false,
  });
  
  // Persist search results across navigation
  const [searchResults, setSearchResults] = useState([]);
  const [searchMeta, setSearchMeta] = useState({
    totalCount: 0,
    searchedTitles: [],
  });

  // Active app mode. Route sync below keeps this aligned with /jobseeker and /jobmatcher.
  const [userRole, setUserRole] = useState('seeker');
  const [initData, setInitData] = useState(null); // cached init payload to avoid duplicate fetches
  const [initLoading, setInitLoading] = useState(false);
  const [initErrorStatus, setInitErrorStatus] = useState(null);

  const handleRoleChange = useCallback(async (role) => {
    const nextRole = normalizeRole(role);
    setUserRole(nextRole);
    if (!isSignedIn) return;
    try {
      await setRole(nextRole);
    } catch (err) {
      console.error('Failed to save role:', err);
    }
  }, [isSignedIn]);

  // Fire CompleteRegistration for brand-new sign-ups (account created within last 60s)
  useEffect(() => {
    if (isLoaded && isSignedIn && user?.createdAt) {
      const ageMs = Date.now() - new Date(user.createdAt).getTime();
      if (ageMs < 60_000) {
        fbTrack('CompleteRegistration', { currency: 'USD', value: 0 });
        trackFunnelEvent('complete_registration', { account_age_ms: ageMs }, { clerkUserId: user.id });
      }
    }
  }, [isSignedIn, isLoaded, user?.createdAt]);

  useEffect(() => {
    let cancelled = false;

    if (isLoaded && isSignedIn) {
      setInitLoading(true);
      setInitErrorStatus(null);
      // Single consolidated init call — replaces getProfile + getRole + getSubscriptionStatus + getUnreadCount
      getInit().then(data => {
        if (cancelled) return;
        const routeRole = getRoleFromPath(window.location.pathname);
        const nextRole = ENABLE_ROLES ? normalizeRole(routeRole || data.role) : 'seeker';
        writeCachedInit(data);
        setInitData(data);
        setUserRole(nextRole);
        setNavbarContext(prev => ({
          ...prev,
          profile: data.profile,
          suggestedTitles: data.profile?.suggested_titles || [],
          isPro: data.is_premium || false,
        }));
        trackFunnelEvent(
          'app_init_loaded',
          {
            role: nextRole,
            is_premium: Boolean(data.is_premium),
          },
          { clerkUserId: user?.id }
        );
        // Saved jobs are secondary to app startup. If they fail, don't block
        // the search/matcher screens with a global API banner.
        loadJobs({ showError: false });
      }).catch(err => {
        if (cancelled) return;
        console.error('Failed to init:', err);
        setInitErrorStatus(err?.response?.status || null);
        const cached = readCachedInit();
        if (cached?.profile) {
          const routeRole = getRoleFromPath(window.location.pathname);
          const nextRole = ENABLE_ROLES ? normalizeRole(routeRole || cached.role) : 'seeker';
          setInitData(cached);
          setUserRole(nextRole);
          setNavbarContext(prev => ({
            ...prev,
            profile: cached.profile,
            suggestedTitles: cached.profile?.suggested_titles || [],
            isPro: cached.is_premium || false,
          }));
        } else {
          setUserRole('seeker');
        }
        trackFunnelEvent(
          'app_init_failed',
          { detail: err?.message || 'init_failed' },
          { clerkUserId: user?.id }
        );
      }).finally(() => {
        if (!cancelled) setInitLoading(false);
      });
    } else if (isLoaded && !isSignedIn) {
      setJobs([]);
      setSearchResults([]);
      setSearchMeta({ totalCount: 0, searchedTitles: [] });
      setError(null);
      setLoading(false);
      setUserRole('seeker');
      setInitData(null);
      setInitErrorStatus(null);
      setInitLoading(false);
    }

    return () => { cancelled = true; };
  }, [isSignedIn, isLoaded]);

  const loadJobs = async ({ showError = true } = {}) => {
    try {
      setLoading(true);
      const data = await getJobs();
      setJobs(data);
      setError(null);
    } catch (err) {
      const status = err.response?.status;
      setJobs([]);
      if (showError && status !== 401 && status !== 403) {
        setError('Failed to load saved jobs. Please try again.');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = async (jobData) => {
    try {
      const newJob = await createJob(jobData);
      setJobs([newJob, ...jobs]);
      setShowForm(false);
      setError(null);
    } catch (err) {
      setError('Failed to create job.');
      console.error(err);
    }
  };

  const handleUpdateJob = async (jobId, updates) => {
    try {
      const updatedJob = await updateJob(jobId, updates);
      setJobs(jobs.map(job => job.id === jobId ? updatedJob : job));
      setEditingJob(null);
      setShowForm(false);
      setError(null);
    } catch (err) {
      setError('Failed to update job.');
      console.error(err);
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await deleteJob(jobId);
      setJobs(jobs.filter(job => job.id !== jobId));
      setError(null);
    } catch (err) {
      setError('Failed to delete job.');
      console.error(err);
    }
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setShowForm(true);
  };

  const handleJobImported = () => {
    loadJobs();
  };

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user?.id) return;
    const intent = consumeSignInIntent();
    if (!intent?.source) return;

    trackFunnelEvent(
      'sign_in_completed',
      {
        source: intent.source,
        ...(intent.properties || {}),
      },
      {
        clerkUserId: user.id,
        path: `${window.location.pathname}${window.location.search}`,
      }
    );
  }, [isLoaded, isSignedIn, user?.id]);

  return (
    <Router>
      <ClerkTokenProvider>
        <RouteTracker />
        <RoleRouteSync userRole={userRole} onRoleChange={handleRoleChange} />
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
          <Navigation 
            jobCount={jobs.length}
            user={user}
            profile={navbarContext.profile}
            resultsCount={navbarContext.resultsCount}
            suggestedTitles={navbarContext.suggestedTitles}
            isPro={navbarContext.isPro}
            userRole={userRole}
            onRoleChange={handleRoleChange}
          />

          <WelcomeModal />

          <Container sx={layoutPatterns.pageContainer}>
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  ...layoutPatterns.section,
                  borderRadius: 2,
                  fontSize: '0.9375rem',
                }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}
            
            <Routes>
              {/* Root redirects to job seeker */}
              <Route path="/" element={<RootRedirect />} />
              <Route path="/gopro" element={<GoPro userRole={userRole} />} />

              {/* ── Job Seeker App ── */}
              <Route path="/jobseeker" element={
                <>
                  <JobSearch 
                  onJobImported={handleJobImported}
                  onContextUpdate={setNavbarContext}
                  searchResults={searchResults}
                  setSearchResults={setSearchResults}
                  searchMeta={searchMeta}
                  setSearchMeta={setSearchMeta}
                  userRole="seeker"
                  initData={initData}
                  initLoading={initLoading}
                  initErrorStatus={initErrorStatus}
                />
                </>
              } />
              <Route path="/jobseeker/saved" element={
                <ProtectedRoute>
                  <MyJobs 
                    jobs={jobs}
                    loading={loading}
                    onEdit={handleEditJob}
                    onDelete={handleDeleteJob}
                    showForm={showForm}
                    setShowForm={setShowForm}
                    editingJob={editingJob}
                    setEditingJob={setEditingJob}
                    handleCreateJob={handleCreateJob}
                    handleUpdateJob={handleUpdateJob}
                  />
                </ProtectedRoute>
              } />
              <Route path="/jobseeker/profile" element={
                <ProtectedRoute>
                  <Profile role="seeker" />
                </ProtectedRoute>
              } />

              {/* ── Job Matcher App (hidden — redirect all traffic to job seeker) ── */}
              <Route path="/jobmatcher" element={<Navigate to="/jobseeker" replace />} />
              <Route path="/jobmatcher/*" element={<Navigate to="/jobseeker" replace />} />

              {/* ── Employer App (hidden — redirect all traffic to job seeker) ── */}
              <Route path="/jobemployer" element={<Navigate to="/jobseeker" replace />} />
              <Route path="/jobemployer/*" element={<Navigate to="/jobseeker" replace />} />

              {/* Legacy redirects for bookmarks */}
              {/* NOTE: /jobs is an SEO route served by FastAPI — do NOT redirect it here */}
              <Route path="/profile" element={<Navigate to="/jobseeker/profile" replace />} />
              <Route path="/matcher" element={<Navigate to="/jobseeker" replace />} />
              <Route path="/matcher/*" element={<Navigate to="/jobseeker" replace />} />
              <Route path="/provider" element={<Navigate to="/jobseeker" replace />} />
              <Route path="/provider/*" element={<Navigate to="/jobseeker" replace />} />
            </Routes>
          </Container>

          {/* Share Widget - Available to everyone */}
          <ShareWidget 
            isOpen={activeWidget === 'share'}
            onOpen={() => setActiveWidget('share')}
            onClose={() => setActiveWidget(null)}
            hideFab={activeWidget !== null}
          />

          {/* Feedback Widget - Only for signed-in users */}
          <SignedIn>
            <FeedbackWidget 
              isOpen={activeWidget === 'feedback'}
              onOpen={() => setActiveWidget('feedback')}
              onClose={() => setActiveWidget(null)}
              hideFab={activeWidget !== null}
            />
          </SignedIn>

          {/* Role Selection Modal removed — users default to seeker, switch via navbar dropdown */}
        </Box>
      </ClerkTokenProvider>
    </Router>
  );
}

export default App;
